<template>
  <LayoutAuthPrimary>
    <div :style="style.container">
      <HeaderLargePrimary
        v-if="state.tenantInvitationCode"
        :truncate="true"
        :style="style.outerHeader"
      >
        Join {{ state.tenant.name }} workspace.
      </HeaderLargePrimary>
      <CardSecondary
        v-if="created"
        :style="style.card"
      >
        <div :style="style.header">
          <HeaderLargePrimary>
            Log in
          </HeaderLargePrimary>
        </div>
        <LogoImagePrimary
          :disabled="true"
          :useWordmark="true"
          :style="getImageStyle"
        />
        <BeatLoaderPrimary v-if="isRequesting" />
        <div
          v-else
          :style="style.textContainer"
        >
          <div :style="style.innerTextContainer">
            <TextRegular :isNowrap="true">
              Enter password for
            </TextRegular>
            <TextEmailPrimary
              :truncate="true"
              :style="style.email"
            >
              {{ form.data.email }}
            </TextEmailPrimary>
          </div>
          <LinkPrimary :to="{ name: 'login' }">
            Switch account
          </LinkPrimary>
          <TextWarningPrimary>
            {{ errors[0] }}
          </TextWarningPrimary>
        </div>
        <div :style="style.footer">
          <FormLoginPrimary :form.sync="form" />
          <LinkMutedPrimary
            :to="{ name: 'password-reset' }"
            :style="style.linkPasswordReset"
          >
            Forgot password?
          </LinkMutedPrimary>
          <ButtonWidePrimary
            :disabled="disableLogin"
            @click="login"
            :style="style.button"
          >
            Continue
          </ButtonWidePrimary>
        </div>
      </CardSecondary>
      <BeatLoaderPrimary v-else />
    </div>
  </LayoutAuthPrimary>
</template>

<script>
  import ApiCurrentUser from '@js/api/current-user'
  import ApiEmailAvailability from '@js/api/email-availability'
  import ApiSsoAuthorizationUrl from '@js/api/sso-authorization-url'
  import ApiTokenPair from '@js/api/token-pair'
  import BeatLoaderPrimary from '@modules/v2/loader/beat-loader-primary'
  import ButtonWidePrimary from '@modules/v2/button/button-wide-primary'
  import CardSecondary from '@modules/v2/card/card-secondary'
  import FormLoginPrimary from '@modules/v2/form/form-login-primary'
  import HeaderLargePrimary from '@modules/v2/header/header-large-primary'
  import HttpStatus from 'http-status-codes'
  import LayoutAuthPrimary from '@modules/v2/layout/layout-auth-primary'
  import LinkMutedPrimary from '@modules/v2/link/link-muted-primary'
  import LinkPrimary from '@modules/v2/link/link-primary'
  import LogoImagePrimary from '@modules/v2/image/logo-image-primary'
  import TextEmailPrimary from '@modules/v2/text/text-email-primary'
  import TextRegular from '@modules/v2/text/text-regular'
  import TextWarningPrimary from '@modules/v2/text/text-warning-primary'
  import { FormLogin } from '@js/forms/login'
  import { getMessageLifetime } from '@js/interface-utils'
  import { Layout } from '@js/styles'

  export default {
    name: 'LoginView',
    components: {
      BeatLoaderPrimary,
      ButtonWidePrimary,
      CardSecondary,
      FormLoginPrimary,
      HeaderLargePrimary,
      LayoutAuthPrimary,
      LinkMutedPrimary,
      LinkPrimary,
      LogoImagePrimary,
      TextEmailPrimary,
      TextRegular,
      TextWarningPrimary,
    },
    props: {
      authorized: {
        type: Boolean,
        required: false,
        default: false,
      },
      state: {
        type: Object,
        required: false,
        default: () => {},
      },
    },
    data: function () {
      return {
        created: false,
        errors: [],
        errorTimer: null,
        form: new FormLogin(),
        isRequesting: true,
        style: {
          button: {
            margin: `${Layout.space.sm} 0 0 0`,
          },
          card: {
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            height: 'fit-content',
            justifyContent: 'space-between',
            maxWidth: '400px',
            padding: `${Layout.space.xl} ${Layout.space.lg}`,
            width: '90%',
          },
          container: {
            alignItems: 'center',
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            justifyContent: 'center',
            padding: `${Layout.space.lg} 0`,
            width: '100%',
          },
          email: {
            padding: `0 0 0 ${Layout.space.xs}`,
          },
          footer: {
            position: 'relative',
            width: '100%',
          },
          header: {
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
          },
          image: {
            margin: '99.5px 0',
            maxWidth: '280px',
            width: '100%',
          },
          innerTextContainer: {
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'center',
            maxWidth: '100%',
            width: 'fit-content',
          },
          linkSignup: {
            margin: `0 0 0 ${Layout.space.xs}`,
          },
          linkPasswordReset: {
            margin: `${Layout.space.md} 0`,
          },
          outerHeader: {
            justifyContent: 'center',
            margin: `0 0 40px 0`,
            padding: `0 ${Layout.space.md}`,
            width: '100%',
          },
          textContainer: {
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
          },
        },
      };
    },
    created: async function () {
      if (this.authorized && !this.state.tenantInvitationCode) {
        this.$router.push({ name: 'tenants' });
      }

      if (
        !this.state.email ||
        this.state.ssoAuthorizationUrl
      ) {
        this.$router.push({ name: 'login' });
      }

      this.form.data.email = this.state.email;
      this.form.data.ssoCode = this.state.ssoCode;
    },
    mounted: async function () {
      if (this.state.ssoCode) {
        await this.login();
      }

      this.isRequesting = await false;
      this.created = await true;
    },
    computed: {
      disableLogin: function () {
        return this.form.errors.length > 0 || this.isRequesting;
      },
      getImageStyle: function () {
        return {
          margin: this.errors.length > 0 ? '99px 0 79px 0' : '99.5px 0',
          maxWidth: '280px',
          width: '100%',
        };
      },
    },
    methods: {
      addError: function (message) {
        if (!message) {
          return;
        }

        clearTimeout(this.errorTimer);
        this.errors = [message];
        this.errorTimer = setTimeout(() => {
          this.errors = [];
        }, getMessageLifetime(message.length, true));
      },
      clearTokensInLocalStorage: function () {
        this.$store.commit('auth/clearAccessToken');
        this.$store.commit('auth/clearRefreshToken');
      },
      fetchCurrentUser: async function () {
        const api = await new ApiCurrentUser();
        const res = await api.get();

      },
      login: async function () {
        try {
          this.isRequesting = await true;
          await this.clearTokensInLocalStorage();
          const payload = await this.form.getPostPayload();
          const query = await {
            invitationCode: this.state.tenantInvitationCode,
          };

          const api = await new ApiTokenPair();
          const res = await api.post(payload, query);

          await this.$store.commit('auth/setTokenPair', res.data);
          await this.$router.push({ name: 'tenants' });
        } catch (error) {
          console.error(error);
          if (
            error?.response?.status === HttpStatus.FORBIDDEN ||
            error?.response?.status === HttpStatus.CONFLICT
          ) {
            this.addError(error?.response?.data?.messages[0]);
          } else {
            this.addError('Incorrect email or password.');
          }
        } finally {
          this.isRequesting = false;
        }
      },
    },
  };
</script>
